import React, { useMemo } from 'react'

import { MAX_MENU_HEIGHT } from 'cube'
import { equals, groupBy } from 'ramda'

import {
  type SelectGroupedOptions,
  type SelectOption,
  Select,
} from '@driftt/dds'

import { useISOCountryData } from './useISOCountryData.hook'

interface CountryCodeSelectProps {
  selectedGeoCountryCodes: string[]
  isDisabled: boolean
  onChange: (geoCountryCodes: string[]) => void
}

/**
 *
 * Manages geo country code selection. Returns a list of selected ISO alpha-2 geo codes represented as strings.
 * For ex, ['US', 'NZ'].
 *
 * @param {CountryCodeSelectProps} CountryCodeSelectProps
 * @returns {JSX.Element} JSX.Element
 */
export const CountryCodeSelect = ({
  selectedGeoCountryCodes = [],
  isDisabled,
  onChange,
}: CountryCodeSelectProps) => {
  const { countryOptions } = useISOCountryData()

  const CountryOptionGroupedByRegion: SelectGroupedOptions<string>[] = useMemo(
    () =>
      Object.entries(
        groupBy((country) => country.region || 'Other', countryOptions)
      ).map(([region, countries]) => ({ label: region, options: countries })),
    [countryOptions]
  )

  const SelectedCountryOptions = useMemo(
    () =>
      selectedGeoCountryCodes.map((geoCode: string) =>
        countryOptions.find((countryOption) =>
          equals(countryOption.value, geoCode)
        )
      ),
    [selectedGeoCountryCodes, countryOptions]
  )

  return (
    <Select
      multi
      allowSelectAll
      label="Select geo country codes"
      placeholder="Select..."
      hideLabel
      options={CountryOptionGroupedByRegion}
      value={SelectedCountryOptions}
      onChange={(options) => {
        const selectedOptions = options as SelectOption<string>[]
        const selectedValues = selectedOptions.map((option) => option.value)
        onChange(selectedValues)
      }}
      isDisabled={isDisabled}
      maxMenuHeight={MAX_MENU_HEIGHT}
    />
  )
}
